import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, XCircle, Download, ScanLine } from "lucide-react";
import api, { API } from "@/lib/apiClient";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS = {
  VALID: { cls: "text-green-700 bg-green-50 border-green-200", icon: ShieldCheck },
  REFUNDED: { cls: "text-amber-700 bg-amber-50 border-amber-200", icon: XCircle },
  CANCELLED: { cls: "text-red-600 bg-red-50 border-red-200", icon: XCircle },
};

function ResultCard({ data, token }) {
  const S = STATUS[data.status] || STATUS.VALID;
  const Icon = S.icon;
  return (
    <div className="bg-white border border-npst-line rounded-md overflow-hidden" data-testid="verify-valid">
      <div className={`flex items-center justify-center gap-2 py-3 border-b ${S.cls}`} data-testid="verify-status">
        <Icon size={18} /> <span className="font-semibold uppercase tracking-[0.14em] text-sm">{data.status}</span>
      </div>
      <div className="divide-y divide-npst-line">
        {[["Receipt No.", data.receipt_no], ["Transaction Date & Time", data.date],
          ["Verification ID", data.verification_id], ["Donor Name", data.donor_name],
          ["Donation Amount", `₹${Number(data.amount).toLocaleString("en-IN")}`], ["Purpose", data.purpose],
          ["Payment Mode", data.payment_mode]].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 px-6 py-3.5 text-sm">
            <span className="text-npst-muted uppercase tracking-wider text-xs font-semibold shrink-0">{k}</span>
            <span className="text-npst-ink font-medium text-right break-all">{v}</span>
          </div>
        ))}
      </div>
      {token && data.status === "VALID" && (
        <div className="p-6 border-t border-npst-line">
          <a href={`${API}/verify-donation/${token}/receipt`} target="_blank" rel="noopener noreferrer" data-testid="verify-download-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-npst-navy text-white text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navyDeep transition-colors"><Download size={15} /> Download Receipt</a>
        </div>
      )}
    </div>
  );
}

export default function VerifyDonation() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  const [receiptNo, setReceiptNo] = useState("");
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (token) api.get(`/verify-donation/${token}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [token]);

  const search = async () => {
    if (!receiptNo.trim()) return;
    setSearching(true); setData(undefined);
    try {
      const { data } = await api.get("/verify-donation/by-receipt", { params: { receipt_no: receiptNo.trim().toUpperCase() } });
      setData(data);
    } catch { setData(null); }
    finally { setSearching(false); }
  };

  return (
    <div className="fade-in max-w-2xl mx-auto px-6 py-16">
      <SEO title="Donation Receipt Verification — National Public Service Trust" description="Verify the authenticity of a National Public Service Trust donation receipt." />
      <div className="text-center mb-8">
        <div className="label-eyebrow text-npst-gold mb-2">National Public Service Trust</div>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-npst-ink">Donation Receipt Verification</h1>
        <p className="text-sm text-npst-muted mt-3">Scan the QR code on your receipt, or enter the receipt number below.</p>
      </div>

      {!token && data === undefined && (
        <div className="bg-white border border-npst-line rounded-md p-6" data-testid="verify-search">
          <label className="label-eyebrow text-slate-500 block mb-2">Receipt Number</label>
          <div className="flex gap-2">
            <Input value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="NPST/DR/2026-27/0001" className="font-mono" data-testid="verify-receipt-input" />
            <Button onClick={search} disabled={searching} data-testid="verify-search-btn"
              className="bg-npst-navy hover:bg-npst-navyDeep shrink-0">
              <ScanLine size={15} className="mr-1.5" /> {searching ? "…" : "Verify"}
            </Button>
          </div>
        </div>
      )}

      {data === undefined && token ? (
        <p className="text-center text-npst-muted animate-pulse">Verifying…</p>
      ) : data === null ? (
        <>
          <div className="bg-red-50 border border-red-200 rounded-md p-8 text-center" data-testid="verify-invalid">
            <XCircle size={40} className="text-red-500 mx-auto mb-3" />
            <h2 className="font-serif text-2xl text-npst-ink">Receipt Not Found</h2>
            <p className="text-sm text-npst-inkSoft mt-2">This verification link or receipt number is invalid, or the receipt does not exist.</p>
          </div>
          {!token && (
            <div className="text-center mt-5">
              <button onClick={() => { setData(undefined); setReceiptNo(""); }} className="text-sm text-npst-navy link-underline font-semibold" data-testid="verify-try-again-btn">Try another receipt number</button>
            </div>
          )}
        </>
      ) : data ? <ResultCard data={data} token={token} /> : null}
    </div>
  );
}
